import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import * as BasicEventHelpers from '../../../helpers/basic-event.helper';
import * as DataConsentHelpers from '../../../helpers/data-consent.helper';
import { BasicEvent } from '../../../models/events/basic-event.model';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow, OneTrustGeoLocationData } from '@core/domain/models/common-window.model';
import { CookieGroup } from '@core/domain/models/user-consent.models';
import { Resolutions } from '@core/domain/state/core.state';
import { CoreOneTrustService } from '@core/infrastructure/one-trust/one-trust.service';
import { getCountryCode, getResolution } from '@core/infrastructure/state/core.selectors';
import { userConsentPoliciesValues } from '@core/services/user-consent/user-consent.service';
import { PublicUser } from '@features/user';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { getReferredSent } from '@shared/analytics/store/analytics.selectors';
import * as PayloadHelpers from '@shared/analytics/ZENIT/helpers/payload.helper';
import { ZenitEventData, ZenitEventName } from '@shared/analytics/ZENIT/models/zenit-general.model';
import { getUserSelector } from '@shared/user/store';

@Injectable()
export class BasicEventParserService {
    // Cambiar a array de parámetros de configuración como los eventos??
    private readonly basicEventConfig: BasicEvent = {
        device: {
            language: null,
            // name: null, // No se puede obtener el nombre (marca-modelo) del cliente
            os: null,
            osVersion: null,
            type: null,
            clientId: null,
        },
        geo: {
            country: null,
            region: null,
            // regionGroupId: null, // No obtenermos este dato del servicio de geolocalización de OneTrust
        },
        identity: {
            userId: null,
            token: null,
            kind: null,
        },
        'privacy-consent': {
            consentedDate: null,
            consentCategories: null,
            // tcString: null, // Parámetro relacionado con IAB, que no tenemos actualmente
            // addtlString: null, // Parámetro relacionado con IAB, que no tenemos actualmente
            // USPrivacyString: null, // Parámetro relacionado con IAB, que no tenemos actualmente
        },
        projectKey: null,
        tenant: null,
        utm: {
            gclid: null,
            source: null,
            medium: null,
            campaign: null,
            term: null,
            content: null,
        },
        technical: {
            navigation: {
                url: null,
            },
        },
        'user-agent': {
            appName: null,
            appVersion: null,
            platform: null,
            uaString: null,
        },
        version: null,
        referrer: null,
    };

    constructor(
        private readonly store: Store,
        private readonly oneTrustService: CoreOneTrustService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(DOCUMENT) private readonly document: Document,
    ) { }

    @BrowserOnly<BasicEventParserService, Observable<BasicEvent>>(of(undefined))
    public getBasicEvent$(eventName: ZenitEventName, eventData: ZenitEventData): Observable<BasicEvent> {
        return combineLatest([
            this.store.select(getResolution),
            this.store.select(getUserSelector),
            this.store.select(getCountryCode),
            this.store.select(routerPublicQueries.getQueryParamsFromRouter),
            this.store.select(getReferredSent),
        ]).pipe(
            map(([resolution, user, storeCountryCode, queryParams, referredSent]) => {
                const navigator: Navigator = this.window.navigator;

                return this.getBasicEventObject(
                    eventName,
                    eventData,
                    this.oneTrustService.getCookieGroupMap,
                    this.oneTrustService.getGeoLocationData(),
                    this.document.cookie,
                    user,
                    storeCountryCode,
                    navigator,
                    resolution,
                    queryParams,
                    referredSent
                );
            }),
        );
    }

    private getBasicEventFilteredParams(
        cookieGroupMap: Record<userConsentPoliciesValues, CookieGroup>,
        currentStore: string
    ) {
        const _basicEventConfig = { ...this.basicEventConfig };
        const params = [];
        const getParamsList = (config) => {
            Object.entries(config).forEach((entry) => {
                if (typeof entry[1] === 'object' && entry[1] !== null) {
                    getParamsList(entry[1]);
                } else {
                    params.push(entry[0]);
                }
            });
        };
        getParamsList(_basicEventConfig);

        return DataConsentHelpers.filterRestrictedParams(params, cookieGroupMap, currentStore);
    }

    private getBasicEventObject(
        eventName: ZenitEventName,
        eventData: ZenitEventData,
        cookieGroupMap: Record<userConsentPoliciesValues, CookieGroup>,
        geoLocationData: OneTrustGeoLocationData,
        cookiesString: string,
        user: PublicUser,
        storeCountryCode: string,
        navigator: Navigator,
        resolution: Resolutions,
        queryParams: Params,
        referredSent: boolean
    ): BasicEvent {
        const filteredParams = this.getBasicEventFilteredParams(cookieGroupMap, storeCountryCode);
        const basicEvent: BasicEvent = JSON.parse(JSON.stringify(this.basicEventConfig));

        filteredParams.forEach((param) => {
            // ORDEN ALFABÉTICO
            switch (param) {
                case 'appName':
                    basicEvent['user-agent'][param] = 'com.inditex.massimodutti';
                    break;
                case 'appVersion':
                    basicEvent['user-agent'][param] = '1.0.0';
                    break;
                case 'campaign':
                case 'content':
                case 'gclid':
                case 'medium':
                case 'source':
                case 'term':
                    basicEvent.utm[param] = BasicEventHelpers.getUtmParam(queryParams, param);
                    break;
                case 'clientId':
                    basicEvent.device[param] = PayloadHelpers.getCookie(cookiesString, '_ga');
                    break;
                case 'consentCategories':
                    basicEvent['privacy-consent'][param] = BasicEventHelpers.getConsentCategories(cookieGroupMap);
                    break;
                case 'consentedDate':
                    basicEvent['privacy-consent'][param] = BasicEventHelpers.getConsentedDate();
                    break;
                case 'country':
                    basicEvent.geo[param] = geoLocationData?.country;
                    break;
                case 'kind':
                    basicEvent.identity[param] = BasicEventHelpers.getUserType(user?.userType);
                    break;
                case 'language':
                    basicEvent.device[param] = BasicEventHelpers.getLanguage(navigator.language);
                    break;
                case 'os':
                    basicEvent.device[param] = BasicEventHelpers.getOs(navigator.userAgent);
                    break;
                case 'osVersion':
                    basicEvent.device[param] = BasicEventHelpers.getOsVersion(navigator.userAgent);
                    break;
                case 'platform':
                    basicEvent['user-agent'][param] = 'web';
                    break;
                case 'projectKey':
                    basicEvent[param] = 'webMD';
                    break;
                case 'region':
                    basicEvent.geo[param] = geoLocationData?.state;
                    break;
                case 'tenant':
                    basicEvent[param] = 'md';
                    break;
                case 'token':
                    basicEvent.identity[param] = BasicEventHelpers.getUserIdToken(user?.identity.userId);
                    break;
                case 'type':
                    basicEvent.device[param] = BasicEventHelpers.getType(resolution);
                    break;
                case 'uaString':
                    basicEvent['user-agent'][param] = navigator.userAgent;
                    break;
                case 'userId':
                    basicEvent.identity[param] = user?.identity.userId.toString();
                    break;
                case 'url':
                    basicEvent.technical.navigation[param] = this.window.location.href;
                    break;
                case 'version':
                    basicEvent.version = BasicEventHelpers.getEventVersion(eventName);
                    break;
                case 'referrer':
                    basicEvent.referrer = BasicEventHelpers.getReferrer(this.document, this.window, referredSent);
                    break;
            }
        });

        return basicEvent;
    }
}
